import { Injectable, OnDestroy, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, Subject, interval, take, takeUntil, tap } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { ConfirmationModalComponent } from '../core/components/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalCloseType } from '../models';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
@Injectable({
  providedIn: 'root',
})
export class CheckForUpdateService implements OnDestroy {
  private readonly _environment = inject(APP_ENV);
  private readonly _dialogService = inject(DialogService);
  private readonly _swUpdate = inject(SwUpdate);

  updateAvailable$ = new BehaviorSubject<boolean>(false);
  saveAndUpdate$ = new BehaviorSubject<boolean>(false);

  private _newVersionNumber = '';
  private _versionReadFromCache$ = new Subject();

  private _destroyed$ = new Subject();
  private _isUpdateAvailable = false;
  private _isModalShowing = false;

  private readonly _dbControlCacheName = 'ngsw:/:db:control';
  private readonly _latestVersionKeyName = '/latest';
  private readonly _versionFileName = '/version.json';

  constructor() {
    setTimeout(() => {
      this._pollForUpdates();
    }, this._environment.serviceWorkerRegistrationDelay + 10000);
  }

  ngOnDestroy(): void {
    this._destroyed$.next(true);
    this._destroyed$.complete();
  }

  get isUpdateAvailable() {
    return this._isUpdateAvailable;
  }

  showUpdateAvailableModal(isStateDirty: boolean) {
    if (!this._isModalShowing) {
      this._isModalShowing = true;

      this._getNewVersionNumber();
      this._versionReadFromCache$
        .pipe(
          take(1),
          tap(() => {
            const modalOptions: any = {
              width: '440px',
              closable: false,
              data: {
                header: 'Clear Update Available',
                icon: 'pi pi-megaphone',
              },
            };

            if (isStateDirty) {
              modalOptions.data = {
                ...modalOptions.data,
                message:
                  '<p class="mt-4">Great news - an updated version of Clear ' +
                  this._newVersionNumber +
                  ' is available! Release notes for this latest version will be posted on <a href="https://www.cmglife.com" target="_blank" class="cmg-link-primary">cmglife.com</a></p><p>Would you like to save before updating to the latest version? If you update without saving, then all recent changes will be lost.</p>',
                rejectLabel: 'Update without Saving',
                acceptLabel: 'Save and Update',
              };
            } else {
              modalOptions.data = {
                ...modalOptions.data,
                message:
                  '<p class="mt-4">Great news - an updated version of Clear ' +
                  this._newVersionNumber +
                  ' is available! Release notes for this latest version will be posted on <a href="https://www.cmglife.com" target="_blank" class="cmg-link-primary">cmglife.com</a></p>',
                rejectLabel: null,
                acceptLabel: 'Update Now',
              };
            }

            const dialogRef = this._dialogService.open(ConfirmationModalComponent, modalOptions);
            dialogRef.onClose.subscribe((modalResult: ConfirmationModalCloseType) => {
              this._isModalShowing = false;

              if (isStateDirty && modalResult === ConfirmationModalCloseType.ClosedViaAcceptButton) {
                this._isUpdateAvailable = true;
                this.saveAndUpdate$.next(true);
              } else {
                document.location.reload();
              }
            });
          }),
        )
        .subscribe();
    }
  }

  private _pollForUpdates() {
    if (this._swUpdate.isEnabled) {
      const delay = this._environment.versionUpdatePollingDelay;
      const pollingInterval$ = interval(delay);

      pollingInterval$.pipe(takeUntil(this._destroyed$)).subscribe(() => {
        try {
          this._swUpdate.checkForUpdate();
        } catch (ex) {
          //
        }
      });

      this._swUpdate.versionUpdates.pipe(takeUntil(this._destroyed$)).subscribe((evt) => {
        if (evt.type === 'VERSION_READY' && !this._isModalShowing) {
          this.updateAvailable$.next(true);
        }
      });
    }
  }

  private _getNewVersionNumber() {
    try {
      let newVersionNumber = '';
      caches
        .open(this._dbControlCacheName)
        .then((dbControlCache) => {
          return dbControlCache.keys().then((cacheRequests) => {
            for (let i = 0; i < cacheRequests.length; i++) {
              const request = cacheRequests[i];
              if (request.url.includes(this._latestVersionKeyName)) {
                return dbControlCache.match(request).then((latestVersionKey) => {
                  return latestVersionKey.json().then((data) => {
                    const latestVersionHash = data.latest;
                    const latestAssetsAppCacheName = 'ngsw:/:' + latestVersionHash + ':assets:app:cache';
                    return caches.open(latestAssetsAppCacheName).then((latestAssetsAppCache) => {
                      return latestAssetsAppCache.keys().then((cacheRequests2) => {
                        for (let j = 0; j < cacheRequests2.length; j++) {
                          const request2 = cacheRequests2[j];
                          if (request2.url.includes(this._versionFileName)) {
                            return latestAssetsAppCache.match(request2).then((versionKey) => {
                              return versionKey.json().then((data2) => {
                                if (data2?.version) {
                                  newVersionNumber = `(v.${data2.version})`;
                                }
                              });
                            });
                          }
                        }
                      });
                    });
                  });
                });
              }
            }
          });
        })
        .catch(() => {
          newVersionNumber = '';
        })
        .finally(() => {
          this._newVersionNumber = newVersionNumber;
          this._versionReadFromCache$.next(true);
        });
    } catch (ex) {
      this._newVersionNumber = '';
      this._versionReadFromCache$.next(true);
    }
  }
}
